import type { AboutData } from '../../models/About';
import { sanitizeRichHtml } from '@lumora/ui';
import EditableField from '../component/EditableField';

interface DisclaimerViewProps {
    content: AboutData | null;
    isEditMode?: boolean;
    setContent?: (data: AboutData) => void;
    onSave?: (data: AboutData) => void;
}

// Fallbacks for About page dependencies
const defaultWarrantyText = `<h2 class="text-2xl font-bold text-red-700 mb-3"><i class="fas fa-shield-alt mr-2"></i>No Implied Warranties</h2><p>Al Amir Auto LLC expressly disclaims all implied warranties arising under state law, including but not limited to the implied warranty of merchantability (that the vehicle is fit for the ordinary purposes for which such vehicles are used) and the implied warranty of fitness for a particular purpose (that the vehicle is suitable for the buyer's specific intended use). The buyer acknowledges that no implied warranties apply to the vehicle purchase.</p>`;
const defaultSalesText = `<h2 class="text-2xl font-bold text-amber-800 mb-3"><i class="fas fa-gavel mr-2"></i>Important Sales Information (AS-IS Policy)</h2><p>Please note that unless explicitly stated otherwise in a separate, signed warranty document provided at the time of sale, all vehicles are sold <strong>AS-IS</strong>. This means the vehicle is sold in its current condition without any express or implied warranties from Al Amir Auto regarding its future performance or condition. The buyer assumes responsibility for any future repairs or maintenance.</p><p>We provide this information clearly because transparency is key to our business. We encourage pre-purchase inspections to ensure you are fully informed and comfortable with your decision. Our goal is to match you with the right car for your needs and budget, building a relationship based on trust.</p>`;

// Fallbacks for Disclaimer specific data
const defaultBuyersResp = `<h2 class="text-2xl font-bold text-red-700 mb-3"><i class="fas fa-user-check mr-2"></i>Buyer's Responsibility & Inspection</h2><p>It is the sole responsibility of the buyer to thoroughly inspect the vehicle to their satisfaction <strong>before</strong> completing the purchase. Al Amir Auto LLC strongly encourages all potential buyers to conduct a comprehensive pre-purchase inspection (PPI) by an independent, qualified mechanic of their choice. Any statements made by dealership representatives regarding the vehicle's condition, history, or features are provided for informational purposes only and do not constitute a warranty or guarantee.</p><p>Failure to inspect the vehicle or obtain a PPI before purchase constitutes a waiver of the buyer's right to object to any defects or conditions discovered after the sale.</p>`;
const defaultInfoAcc = `<h2 class="text-2xl font-bold text-red-700 mb-3"><i class="fas fa-info-circle mr-2"></i>Information Accuracy</h2><p>While Al Amir Auto LLC makes reasonable efforts to ensure the accuracy of information provided on this website and in marketing materials (including vehicle specifications, features, options, mileage, history reports, and pricing), errors, omissions, or discrepancies may occur. We do not warrant the accuracy or completeness of this information. Buyers are advised to independently verify all vehicle details, specifications, and history prior to purchase. Vehicle prices, availability, and specifications are subject to change without prior notice.</p>`;


const DisclaimerView = ({ content, isEditMode = false, setContent, onSave }: DisclaimerViewProps) => {

    // Builds out missing structure for the 'aboutPage' node
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createAboutSetter = (fieldKey: string) => (updatedField: any) => {
        if (!setContent) return;
        setContent({
            ...(content || {}),
            aboutPage: {
                ...(content?.aboutPage || {}),
                [fieldKey]: updatedField
            }
        } as AboutData);
    };

    // Builds out missing structure for the 'disclaimerPage' node
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createDisclaimerSetter = (fieldKey: string) => (updatedField: any) => {
        if (!setContent) return;
        setContent({
            ...(content || {}),
            disclaimerPage: {
                ...(content?.disclaimerPage || {}),
                [fieldKey]: updatedField
            }
        } as AboutData);
    };

    // Always ensure objects exist to bind the EditableField to
    const salesInfoTextObj = content?.aboutPage?.salesInfoTextHTML || { value: defaultSalesText };
    const warrantyTextObj = content?.aboutPage?.warrantyTextHTML || { value: defaultWarrantyText };
    const buyersRespObj = content?.disclaimerPage?.buyersResponsibilityHTML || { value: defaultBuyersResp };
    const infoAccObj = content?.disclaimerPage?.informationAccuracyHTML || { value: defaultInfoAcc };

    return (
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 w-full flex-grow">
            <div className="bg-white p-8 md:p-12 rounded-xl shadow-sm border border-gray-100">
                <h1 className="text-4xl md:text-5xl font-bold text-center mb-10 uppercase text-gray-800">Legal Disclaimer</h1>
                <div className="space-y-10 text-lg leading-relaxed text-gray-700">
                    
                    <div className="mb-8">
                        {isEditMode ? (
                            <EditableField
                                contentKey="value"
                                content={salesInfoTextObj}
                                setContent={createAboutSetter('salesInfoTextHTML')}
                                isEditMode={isEditMode}
                                onSave={onSave}
                            />
                        ) : (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(salesInfoTextObj.value || '') }} />
                        )}
                    </div>

                    <div className="mb-8">
                        {isEditMode ? (
                            <EditableField
                                contentKey="value"
                                content={warrantyTextObj}
                                setContent={createAboutSetter('warrantyTextHTML')}
                                isEditMode={isEditMode}
                                onSave={onSave}
                            />
                        ) : (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(warrantyTextObj.value || '') }} />
                        )}
                    </div>

                    <div className="mb-8">
                        {isEditMode ? (
                            <EditableField
                                contentKey="value"
                                content={buyersRespObj}
                                setContent={createDisclaimerSetter('buyersResponsibilityHTML')}
                                isEditMode={isEditMode}
                                onSave={onSave}
                            />
                        ) : (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(buyersRespObj.value || '') }} />
                        )}
                    </div>

                    <div className="mb-8">
                        {isEditMode ? (
                            <EditableField
                                contentKey="value"
                                content={infoAccObj}
                                setContent={createDisclaimerSetter('informationAccuracyHTML')}
                                isEditMode={isEditMode}
                                onSave={onSave}
                            />
                        ) : (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(infoAccObj.value || '') }} />
                        )}
                    </div>
                    
                    <div className="border-t border-gray-200 pt-8">
                        <p className="font-semibold text-center text-gray-800">By proceeding with the purchase of a vehicle from Al Amir Auto LLC, you affirm that you have read, fully understood, and agreed to be bound by the terms of this AS-IS disclaimer.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DisclaimerView;